import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Coins } from "lucide-react";
import api from "@/lib/api";
import { money, dateFmt } from "@/lib/format";

const todayISO = () => new Date().toISOString().slice(0, 10);
const TYPE_ORDER = ["roi", "level", "matching", "salary", "royalty", "turnover", "points"];
const TYPE_COLOR = { roi: "text-emerald-400", level: "text-sky-400", matching: "text-amber-400", salary: "text-purple-400", royalty: "text-pink-400", turnover: "text-orange-400", points: "text-slate-300" };

export default function AdminIncomeReports() {
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(todayISO());
  const [data, setData] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/admin/reports/incomes", { params: { start, end } })
      .then(({ data }) => setData(data)).catch(() => toast.error("Load failed"));
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const byType = data?.by_type || {};
  const types = TYPE_ORDER.filter((t) => t in byType);
  const items = (data?.items || []).filter((i) => typeFilter === "all" || i.type === typeFilter);

  return (
    <div className="space-y-6" data-testid="admin-income-reports-page">
      <h1 className="font-display text-2xl font-bold flex items-center gap-2"><Coins className="w-6 h-6 text-amber-400" /> Released Incomes Report</h1>

      <div className="glass rounded-2xl p-5 flex flex-wrap items-end gap-4">
        <div><label className="text-xs text-amber-400 uppercase tracking-wider">From</label>
          <input data-testid="income-start-date" type="date" value={start} onChange={(e) => setStart(e.target.value)}
            className="mt-1 block bg-[#0D0E12] border border-amber-500/20 rounded-xl px-4 py-2.5 outline-none text-sm" /></div>
        <div><label className="text-xs text-amber-400 uppercase tracking-wider">To</label>
          <input data-testid="income-end-date" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
            className="mt-1 block bg-[#0D0E12] border border-amber-500/20 rounded-xl px-4 py-2.5 outline-none text-sm" /></div>
        <button data-testid="income-apply-btn" onClick={load} className="gold-btn px-5 py-2.5 rounded-xl text-sm font-semibold">Apply</button>
        <div className="ml-auto text-right">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Total Released</div>
          <div className="font-mono-fin text-2xl font-bold gold-text" data-testid="income-total">{money(data?.total || 0)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <button onClick={() => setTypeFilter("all")} data-testid="income-filter-all"
          className={`glass rounded-2xl p-4 text-left transition-all ${typeFilter === "all" ? "ring-2 ring-amber-500" : ""}`}>
          <div className="text-xs text-slate-400 uppercase tracking-wider">All Types</div>
          <div className="font-mono-fin text-lg font-bold mt-1">{money(data?.total || 0)}</div>
        </button>
        {types.map((t) => (
          <button key={t} onClick={() => setTypeFilter(t)} data-testid={`income-filter-${t}`}
            className={`glass rounded-2xl p-4 text-left transition-all ${typeFilter === t ? "ring-2 ring-amber-500" : ""}`}>
            <div className="text-xs text-slate-400 uppercase tracking-wider capitalize">{t}</div>
            <div className={`font-mono-fin text-lg font-bold mt-1 ${TYPE_COLOR[t] || ""}`}>{money(byType[t] || 0)}</div>
          </button>
        ))}
      </div>

      <div className="glass rounded-2xl p-6 overflow-x-auto" data-testid="income-report-table">
        <h2 className="font-display font-bold mb-3">Entries ({items.length}{data?.count > (data?.items?.length || 0) ? ` of ${data.count}` : ""})</h2>
        <table className="w-full text-sm">
          <thead><tr className="text-amber-400 uppercase text-xs tracking-wider border-b border-amber-500/20">
            <th className="text-left py-2">User</th><th className="text-left py-2">Type</th><th className="text-left py-2">Description</th><th className="text-right py-2">Amount</th><th className="text-right py-2">Date</th>
          </tr></thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-b border-slate-800/60">
                <td className="py-2 text-slate-300">{i.user_email || "-"}</td>
                <td className="py-2"><span className={`text-[10px] px-2 py-1 rounded-full bg-amber-500/10 uppercase ${TYPE_COLOR[i.type] || "text-slate-300"}`}>{i.type}</span></td>
                <td className="py-2 text-slate-500 text-xs">{i.description}</td>
                <td className={`py-2 text-right font-mono-fin ${TYPE_COLOR[i.type] || ""}`}>{money(i.amount)}</td>
                <td className="py-2 text-right text-slate-500 text-xs">{dateFmt(i.created_at)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-500">No income entries in range</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
